import { isInstance, notEmpty } from "@de-xima/xima-common-js-lang";
import jQuery from "jquery";

import type { IComponentHandlerCallbacks, IInsertData } from "../../Header.ts";
import { findWidgetForId } from "../../Util.ts";
import { AbstractComponentHandler } from "../AbstractComponentHandler.ts";

/**
 * Handler for components of type `primefacesExtensions:codeMirror`.
 */
export class CodeMirrorHandler extends AbstractComponentHandler {
  override accepts($element: JQuery): boolean {
    if (this._getCodeMirror($element) !== undefined) {
      return true;
    }
    // Element with class CodeMirror does not exist yet when CodeMirror was not loaded yet.
    const $textarea = $element.find("textarea");
    if ($textarea.next().text().indexOf("ExtCodeMirror") >= 0) {
      return true;
    }
    const widget = findWidgetForId($textarea.attr("id") ?? "", PrimeFaces.widget.BaseWidget);
    return widget !== undefined && isInstance(widget, PrimeFaces.widget.ExtCodeMirror);
  }

  override focus($element: JQuery): void {
    const cm = this._getCodeMirror($element);
    cm?.focus();
  }

  override getInputElement($element: JQuery): JQuery {
    const cm = this._getCodeMirror($element);
    return cm !== undefined ? jQuery(cm.getWrapperElement()) : jQuery();
  }

  override initialize($element: JQuery, callbacks: IComponentHandlerCallbacks): boolean {
    // Check if the CodeMirror is already ready
    if (this._isReady($element)) {
      this._initialize($element, callbacks);
      return true;
    }
    // Otherwise, we need to wait for the CodeMirror to load.
    const observer = new MutationObserver((_mutations, o) => {
      if (this._isReady($element)) {
        // We do not need to listen to any further changes, so unregister for performance.
        o.disconnect();
        this._initialize($element, callbacks);
      }
    });
    const target = $element.get(0);
    if (target !== undefined) {
      observer.observe(target, { childList: true, subtree: true });
    }
    return true;
  }

  override insertText($element: JQuery, data: IInsertData): void {
    const cm = this._getCodeMirror($element);
    if (cm !== undefined) {
      const selection = cm.getDoc().getSelection();
      const cursor = cm.getDoc().getCursor();
      const adjustedText = notEmpty(selection) ? `${data.content} ` : data.content;
      cm.getDoc().replaceSelection(adjustedText, "around");
      if (notEmpty(selection)) {
        const anchor = {
          ch: cursor.ch + adjustedText.length,
          line: cursor.line,
        };
        cm.getDoc().setSelection(anchor, anchor);
      }
    }
  }

  /**
   * @param $element Input component.
   * @returns The code mirror instance.
   */
  private _getCodeMirror($element: JQuery): CodeMirror.Editor | undefined {
    const $cm = $element.find(".CodeMirror");
    if ($cm.length !== 1) {
      return undefined;
    }
    // @ts-expect-error Bad practice, but this property was added by code mirror
    return $cm.get(0).CodeMirror ?? undefined;
  }

  /**
   * Same as the initialize method, but called once the code mirror
   * is ready and initialized.
   * @param $element Code mirror input element.
   * @param callbacks Callbacks we should invoke.
   */
  private _initialize($element: JQuery, callbacks: IComponentHandlerCallbacks): void {
    const cm = this._getCodeMirror($element);
    if (cm !== undefined) {
      const extraKeys = cm.getOption("extraKeys") ?? {};
      // Listen to CTRL+Space
      cm.setOption(
        "extraKeys",
        jQuery.extend({}, extraKeys, {
          "Ctrl-Space"() {
            callbacks.onPlaceholderRequested();
          },
        }),
      );
      // Listen to focus
      cm.on("focus", () => callbacks.onFocus());
    }
  }

  /**
   * @param $element Input component.
   * @returns Whether the code mirror was already initialized.
   */
  private _isReady($element: JQuery) {
    return this._getCodeMirror($element) !== undefined;
  }
}
